import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import api from '../../services/api';
import { waitForJob } from '../../utils/waitForJob';
import { categoryPalette } from '../../theme/tokens';

/**
 * Epic work-type classification panel.
 *
 * Epics carry the attribution for every ticket beneath them, so they are
 * decided deliberately:
 *   - owner overrides (Support Intake / Tech Debt / Bugs) are absolute
 *   - Jev classifications at or above the confidence threshold are applied
 *     automatically
 *   - everything less certain lands in the table below for the owner to
 *     pick, pre-selected with Jev's suggestion but never auto-committed
 */
const EpicClassificationPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [classifying, setClassifying] = useState(false);
  const [progress, setProgress] = useState(null);
  const [choices, setChoices] = useState({});
  const [savingKey, setSavingKey] = useState(null);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/categorisation/epics');
      setData(res.data);
      // Pre-select Jev's suggestion so confirming a correct guess is one click.
      const next = {};
      for (const row of res.data.review || []) {
        if (row.suggestedWorkType) next[row.epicKey] = row.suggestedWorkType;
      }
      setChoices((prev) => ({ ...next, ...prev }));
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const runClassification = async () => {
    setClassifying(true);
    setError(null);
    setMessage(null);
    setProgress(null);
    try {
      const res = await api.post('/categorisation/epics/classify', { projectKey: 'BA' });
      const result = await waitForJob(api, res.data.jobId, {
        onProgress: (p) => setProgress(p),
      });
      setMessage(result?.message || 'Classification finished.');
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setClassifying(false);
      setProgress(null);
    }
  };

  const confirm = async (epicKey) => {
    const workType = choices[epicKey];
    if (!workType) return;
    setSavingKey(epicKey);
    setError(null);
    try {
      await api.put(`/categorisation/epics/${encodeURIComponent(epicKey)}`, { workType });
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setSavingKey(null);
    }
  };

  const summary = data?.summary;
  const workTypes = data?.workTypes || [];
  const threshold = data?.threshold;

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h6">Epic work types</Typography>
            <Typography variant="body2" color="text.secondary">
              Epics set the attribution for every ticket beneath them. Confident
              classifications are applied automatically
              {typeof threshold === 'number' ? ` (${Math.round(threshold * 100)}% or higher)` : ''};
              the rest wait for your decision.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AutoAwesomeIcon />}
            onClick={runClassification}
            disabled={classifying || loading}
          >
            {classifying ? 'Classifying…' : 'Classify epics with Jev'}
          </Button>
        </Stack>

        {classifying && progress && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption">
              Classified {progress.processed} of {progress.total} · {progress.autoApplied} automatic ·{' '}
              {progress.queued} for review
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progress.total ? (progress.processed / progress.total) * 100 : 0}
              sx={{ mt: 0.5 }}
            />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {message && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage(null)}>
            {message}
          </Alert>
        )}

        {summary && (
          <Stack direction="row" spacing={3} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Epics classified
              </Typography>
              <Typography variant="h5">{summary.confirmed}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Awaiting your decision
              </Typography>
              <Typography variant="h5" color={summary.needsReview ? 'warning.main' : 'success.main'}>
                {summary.needsReview}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 220 }}>
              <Typography variant="caption" color="text.secondary">
                Confirmed by work type
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                {Object.entries(summary.byWorkType || {}).map(([type, count]) => (
                  <Chip
                    key={type}
                    size="small"
                    label={`${type} ${count}`}
                    sx={{
                      bgcolor: categoryPalette[type]?.soft,
                      color: categoryPalette[type]?.text,
                    }}
                  />
                ))}
              </Stack>
            </Box>
          </Stack>
        )}

        {Boolean(data?.overrides?.length) && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Your standing epic rules
            </Typography>
            <Stack spacing={0.5} sx={{ mb: 1 }}>
              {data.overrides.map((o) => (
                <Typography key={o.description} variant="caption" color="text.secondary">
                  • {o.description}
                </Typography>
              ))}
            </Stack>
          </>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" gutterBottom>
          Epics needing your decision {summary ? `(${summary.needsReview})` : ''}
        </Typography>

        {!loading && !data?.review?.length && (
          <Typography variant="body2" color="text.secondary">
            Nothing waiting. Run a classification to check for new epics.
          </Typography>
        )}

        {Boolean(data?.review?.length) && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Epic</TableCell>
                <TableCell>Summary</TableCell>
                <TableCell>Jev suggests</TableCell>
                <TableCell>Your decision</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {data.review.map((row) => (
                <TableRow key={row.epicKey}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.epicKey}</TableCell>
                  <TableCell>{row.summary}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {row.suggestedWorkType ? (
                      <Chip
                        size="small"
                        label={`${row.suggestedWorkType}${
                          row.confidence !== null && row.confidence !== undefined
                            ? ` · ${Math.round(row.confidence * 100)}%`
                            : ''
                        }`}
                        sx={{
                          bgcolor: categoryPalette[row.suggestedWorkType]?.soft,
                          color: categoryPalette[row.suggestedWorkType]?.text,
                        }}
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        no suggestion
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <TextField
                      select
                      size="small"
                      value={choices[row.epicKey] || ''}
                      onChange={(e) =>
                        setChoices((prev) => ({ ...prev, [row.epicKey]: e.target.value }))
                      }
                      sx={{ minWidth: 170 }}
                    >
                      <MenuItem value="">Choose…</MenuItem>
                      {workTypes.map((t) => (
                        <MenuItem key={t} value={t}>
                          {t}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={!choices[row.epicKey] || savingKey === row.epicKey}
                      onClick={() => confirm(row.epicKey)}
                    >
                      {savingKey === row.epicKey ? 'Saving…' : 'Confirm'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

export default EpicClassificationPanel;
